import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const PREMIUM_FLAVORS = new Set(["Nido com Nutella", "Ferrero Rocher"]);
const SURCHARGE = 0.15;

export function computeLineSurcharge(item) {
  const flavors = (item.selected_flavors && item.selected_flavors.brigadeiros) || [];
  if (!flavors.length) return 0;
  const premium = flavors.filter((f) => PREMIUM_FLAVORS.has(f)).length;
  if (!premium) return 0;
  let brigUnits = item.pack_size;
  if (item.category === "kits") {
    const m = (item.variant_label || "").match(/(\d+)\s+brigadeiros/);
    if (m) brigUnits = parseInt(m[1], 10);
  }
  const unitsPerFlavor = brigUnits / flavors.length;
  return Math.round(unitsPerFlavor * premium * SURCHARGE * item.quantity * 100) / 100;
}

const flavorsKey = (flavors) => {
  if (!flavors) return "";
  return Object.keys(flavors).sort()
    .map((k) => `${k}:${[...(flavors[k] || [])].sort().join(",")}`)
    .join("|");
};

const customKey = (c) => c ? `${c.theme || ""}|${c.colors || ""}` : "";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("caseirinhos_cart") || "[]"); }
    catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("caseirinhos_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, variant, qty = 1, selectedFlavors = {}, customization = null) => {
    const lineKey = `${product.id}__${variant.id}__${flavorsKey(selectedFlavors)}__${customKey(customization)}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.line_key === lineKey);
      if (existing) {
        return prev.map((i) => i.line_key === lineKey ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [
        ...prev,
        {
          line_key: lineKey,
          product_id: product.id,
          name: product.name,
          category: product.category,
          image_url: product.image_url,
          variant_id: variant.id,
          variant_label: variant.label,
          pack_size: variant.pack_size,
          price: variant.price,
          selected_flavors: selectedFlavors,
          customization,
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  const updateQty = (line_key, quantity) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.line_key !== line_key));
    } else {
      setItems((prev) => prev.map((i) => (i.line_key === line_key ? { ...i, quantity } : i)));
    }
  };

  const removeItem = (line_key) => setItems((prev) => prev.filter((i) => i.line_key !== line_key));
  const clear = () => setItems([]);

  const baseSubtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const surchargeTotal = items.reduce((s, i) => s + computeLineSurcharge(i), 0);
  const subtotal = Math.round((baseSubtotal + surchargeTotal) * 100) / 100;
  const totalCount = items.reduce((s, i) => s + i.quantity, 0);

  const totals = useMemo(
    () => ({ subtotal, surchargeTotal, baseSubtotal, totalCount }),
    [subtotal, surchargeTotal, baseSubtotal, totalCount]
  );

  const value = useMemo(
    () => ({ items, addItem, updateQty, removeItem, clear, ...totals, open, setOpen }),
    [items, totals, open]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
